import type { RootEntityType } from '@/modules/elements/models/Element'
import type User from '@/modules/user/models/User'
import type { AnyExtension } from '@tiptap/core'
import { Fragment, Node as ProseMirrorNode, Slice } from 'prosemirror-model'
import type { EditorView } from 'prosemirror-view'
import type { AddElementData } from './tiptap-commands'

/**
 * Everything an editor's extensions take from their host, read when they run rather than when they are built. Every
 * editor has the same superset schema (mentions, slash commands), so one pooled editor can serve hosts with different
 * features by rebinding this object (`Object.assign`) on each activation.
 */
export type EditorContext = {
  /** Parent of the popups the editor opens (slash menu, mention list, embed link menu) */
  parent: Vue
  /** Turns @-mentions on; without it the mention menu never opens and pasted mentions become plain text */
  getUsers?: () => User[]
  slashCommands: boolean
  /** When `content`, slash menu lists only commands with `scope: 'content'` */
  slashScope?: 'all' | 'content'
  /**
   * Bumped whenever a pooled editor parks or binds to a new target. Slash/mention UI captures this at open time so
   * async picks cannot run with a later target's handlers.
   */
  activationToken?: number
  handleKeyDown?: (view: EditorView, event: KeyboardEvent, extensions: AnyExtension[]) => boolean | void
  addElementHandler?: (...data: AddElementData[]) => void
  /** Also turns on the embed link menu when a pasted link can become an embed or bookmark */
  replaceElementHandler?: (data: AddElementData) => void
  /** A table cell: Bookmark and Embed become a card or iframe node inside the cell, since it holds no elements */
  embedInline?: boolean
  readOnlyTaskItemChecked?: (node: ProseMirrorNode, checked: boolean) => boolean
  handlePaste?: (view: EditorView, event: ClipboardEvent, slice: Slice) => boolean
  handleDrop?: (view: EditorView, event: DragEvent, slice: Slice, moved: boolean) => boolean
  /** Filters the slash menu; defaults to the parent's `rootEntityType` prop */
  rootEntityType?: RootEntityType
}

export type EmbedMenuOption = 'bookmark' | 'link' | 'embed'

/** What the embed link menu lists for this host after a link is pasted; null when the host cannot turn it into one. */
export function embedMenuOptions(ctx: EditorContext | null | undefined): EmbedMenuOption[] | null {
  return ctx?.replaceElementHandler || ctx?.embedInline ? ['bookmark', 'link', 'embed'] : null
}

/** A pasted or dropped slice with its mention nodes turned back into the `@name` text they render as. */
export function stripMentions(slice: Slice): Slice {
  return new Slice(stripMentionsFrom(slice.content), slice.openStart, slice.openEnd)
}

function stripMentionsFrom(fragment: Fragment): Fragment {
  let changed = false
  const children: ProseMirrorNode[] = []
  fragment.forEach(node => {
    if (node.type.name === 'mention') {
      changed = true
      const { name } = node.attrs
      if (name) children.push(node.type.schema.text(`@${name}`, node.marks))
    } else if (node.childCount) {
      const content = stripMentionsFrom(node.content)
      if (content !== node.content) changed = true
      children.push(content === node.content ? node : node.copy(content))
    } else {
      children.push(node)
    }
  })
  // fromArray joins the new text with its neighbours, as parsing the HTML without a mention node did
  return changed ? Fragment.fromArray(children) : fragment
}
